import {
  applyStatus,
  CONTAGION_MAX_TARGETS,
  CONTAGION_RADIUS,
  DEEP_FREEZE_DURATION,
  effectiveArmorValue,
  effectiveSpeed,
  findStatus,
  hasStatus,
  SHOCKWAVE_BONUS_DAMAGE,
  SHOCKWAVE_INNER_RADIUS,
  tickStatusEffects,
  type TowerEffectSpec
} from './effects'
import {
  createTowerPerformance,
  type ApplyDamageResult,
  type DamageSource,
  type TowerPerformanceRecord
} from './combatStats'
import {
  ENEMY_CATALOG,
  ENEMY_TRAY_ORDER,
  createEnemyInstance,
  enemyMovementClass,
  enemyRouteKey,
  type EnemyDefinition,
  type EnemyInstance
} from './enemies'
import { ENEMY_DEFINITIONS_V2 } from '../combat/enemy/catalog'
import { resolveEnemyStats } from '../combat/enemy/resolveEnemyStats'
import { resolveDamage, resolveTypeMultiplier } from '../combat/damage/resolveDamage'
import type { DamageType } from '../combat/types/primitives'
import type { StatusHostTraits } from '../combat/status/resolveStatusApplication'
import { scaleReward } from '../combat/difficulty/catalog'
import { TOWER_DEFINITIONS_V2 } from '../combat/tower/catalog'
import { resolveTowerStats } from '../combat/tower/resolveTowerStats'
import {
  emptyResearchState,
  isResearchTowerId,
  type ResearchState,
  type ResearchTowerId
} from '../combat/research/types'
import { researchModifiersForTower } from '../combat/research/modifiers'
import { sanitizeResearchState } from '../combat/research/purchase'
import { Grid } from './grid'
import {
  defaultRouteId,
  findRoute,
  normalizeMapRoutes,
  positionAt,
  routeTrack,
  type PathTrack,
  type RouteDefinition
} from './path'
import type { ProjectileInstance } from './projectiles'
import { distanceCells, isTargetingMode, selectMultiLockTargets, selectTarget, type TargetingMode } from './targeting'
import {
  footprintCenter,
  footprintCells,
  levelCost,
  maxLevel,
  sellRefund,
  TOWER_CATALOG,
  TOWER_TRAY_ORDER,
  towerDamageType,
  type TowerDefinition,
  type TowerInspectSnapshot,
  type TowerInstance,
  type TowerLevelDefinition
} from './towers'
import type { GridPosition, MapDefinition, WaveDefinition, WaveGroupDefinition } from './types'
import { summarizeWave, type WaveSummary } from '../../editor/waveHelpers'
import {
  computeEarlyStartBonus,
  DEFAULT_WAVE_PACING,
  prepCountdownDisplaySeconds,
  prepRemainingSeconds,
  type WavePacingConfig
} from './wavePacing'

export type SessionState = 'READY' | 'PLAYING' | 'VICTORY' | 'DEFEAT'

export type SimulationSpeed = 1 | 2 | 3

export type PlaceTowerReason =
  | 'UNKNOWN_TOWER'
  | 'OUT_OF_BOUNDS'
  | 'INVALID_TERRAIN'
  | 'OCCUPIED'
  | 'INSUFFICIENT_GOLD'
  | 'NOT_ACTIVE'
  | 'PAUSED'

export type PlaceTowerResult =
  | { ok: true; tower: TowerInstance }
  | { ok: false; reason: PlaceTowerReason }

export type UpgradeTowerReason =
  | 'UNKNOWN_TOWER'
  | 'MAX_LEVEL'
  | 'INSUFFICIENT_GOLD'
  | 'NOT_ACTIVE'

export type UpgradeTowerResult =
  | { ok: true; tower: TowerInstance; cost: number }
  | { ok: false; reason: UpgradeTowerReason }

export type SellTowerReason = 'UNKNOWN_TOWER' | 'NOT_ACTIVE'

export type SellTowerResult =
  | { ok: true; refund: number }
  | { ok: false; reason: SellTowerReason }

export type SetTargetingReason = 'UNKNOWN_TOWER' | 'INVALID_MODE' | 'NOT_ACTIVE'

export type SetTargetingResult =
  | { ok: true; tower: TowerInstance; targetingMode: TargetingMode }
  | { ok: false; reason: SetTargetingReason }

export type StartWaveReason =
  | 'NOT_READY'
  | 'NO_WAVES'
  | 'PAUSED'
  | 'MATCH_OVER'

export type StartWaveResult =
  | { ok: true; waveIndex: number }
  | { ok: false; reason: StartWaveReason }

import { pushCombatFx, type CombatFx } from './presentation'

export type { CombatFx } from './presentation'

/** View snapshot for React. It mirrors the session and does not simulate. */
export interface WavePreviewChip {
  /** Enemy family id (for tutorial / aggregation). */
  id: string
  /** Unique chip key when same family appears at multiple levels/ranks. */
  chipKey: string
  name: string
  count: number
  color: number
  /** Authored enemy level — independent of difficulty scaling. */
  enemyLevel: number
  /** AIR vs GROUND for intel iconography. */
  movementClass?: 'GROUND' | 'AIR'
  /** Optional rank hint when composition uses non-NORMAL ranks. */
  enemyRank?: string
  /** Compact lane mark (A/B/C/AIR) when the stage uses multiple routes. */
  laneLabel?: string
}

function laneLabelForGroup(
  routeId: string | undefined,
  movement: 'GROUND' | 'AIR' | undefined
): string {
  // Physical lane id wins for path intel; movementClass is a separate chip.
  if (routeId?.startsWith('air')) return 'AIR'
  if (!routeId) return movement === 'AIR' ? 'AIR' : 'A'
  if (routeId === 'ground-a') return 'A'
  if (routeId === 'ground-b') return 'B'
  if (routeId === 'ground-c') return 'C'
  const m = /^ground-([a-z])$/i.exec(routeId)
  if (m) return m[1]!.toUpperCase()
  return 'A'
}

export interface WavePreviewView {
  waveNumber: number
  waveCount: number
  totalEnemies: number
  reward: number
  chips: WavePreviewChip[]
}

export interface WaveProgressView {
  total: number
  spawned: number
  alive: number
  /** Remaining to spawn + currently alive (unresolved this wave). */
  unresolved: number
}

export interface SessionView {
  lives: number
  gold: number
  state: SessionState
  elapsedTime: number
  defeatedEnemies: number
  paused: boolean
  simulationSpeed: SimulationSpeed
  /** 1-based wave number for UI (Wave 1 / 5). */
  waveNumber: number
  waveCount: number
  wavesCompleted: number
  waveLabel: string
  canStartWave: boolean
  /** True while READY and a prep countdown is active before the next wave. */
  isPrepCountdown: boolean
  /** Seconds left before auto-start (ceil for HUD). */
  prepCountdownRemaining: number
  /** Total seconds for the current prep phase. */
  prepCountdownTotal: number
  /** Coins granted if the player launches early now (0 when none). */
  earlyStartBonusPreview: number
  towerCount: number
  /** Upcoming / current wave composition (READY or PLAYING). */
  wavePreview: WavePreviewView | null
  /** Active wave spawn/alive counters while PLAYING. */
  waveProgress: WaveProgressView | null
  /** Last wave completion reward (0 when none / not just cleared). */
  lastWaveReward: number
  /** Match-level combat aggregates. */
  totalDamageDealt: number
  totalShotsFired: number
  totalTowerInvestment: number
  totalSellRefunds: number
  /** Campaign identity when opened via createCampaignSession. */
  campaign: {
    campaignId: string
    chapterId: string
    stageId: string
    difficultyId: string
    mapId: string
    displayLabel: string
  } | null
  /** Research ranks snapshotted at session create (balance telemetry). */
  research: ResearchState
}

/** Dev/debug snapshot of the wave runner. */
export interface WaveDebugSnapshot {
  state: SessionState
  waveIndex: number
  groupIndex: number
  spawnedInGroup: number
  spawningComplete: boolean
  spawnTimer: number
  livingEnemies: number
  waveRewardGranted: boolean
}

export interface GameSessionOptions {
  enemies?: Record<string, EnemyDefinition>
  towers?: Record<string, TowerDefinition>
  /** Immutable campaign identity — never mutated by simulation. */
  campaignContext?: import('../campaign/createCampaignSession').CampaignSessionContext
  /**
   * Difficulty stat policy. Identity (all 1s) when omitted — legacy maps / editor.
   * Campaign sessions pass resolveDifficultyRules(..., 'provisional').
   */
  difficultyPolicy?: import('../combat/difficulty/catalog').DifficultyStatPolicy
  /**
   * Research snapshot for this match. Copied at construct; never re-read from storage.
   * Omitted / undefined → empty (R0). GameSession never touches localStorage.
   */
  researchSnapshot?: ResearchState
  /** Wave prep / early-launch tuning. Defaults when omitted. */
  wavePacing?: Partial<WavePacingConfig>
}

export function formatSessionTime(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(total / 60)
  const secs = total % 60
  return `${minutes}:${secs.toString().padStart(2, '0')}`
}

/**
 * Authoritative match. Phaser and React only read what this produces.
 */
export class GameSession {
  readonly map: MapDefinition
  readonly grid: Grid
  /** Immutable campaign identity when present. */
  readonly campaignContext: import('../campaign/createCampaignSession').CampaignSessionContext | null
  /** Difficulty scaling policy (identity when legacy / omitted). */
  readonly difficultyPolicy: import('../combat/difficulty/catalog').DifficultyStatPolicy
  /**
   * Research ranks frozen at session create.
   * Changing persistent Research mid-match does not affect this session.
   */
  readonly researchSnapshot: ResearchState
  readonly wavePacing: WavePacingConfig

  gold: number
  lives: number
  state: SessionState = 'READY'
  enemies: EnemyInstance[] = []
  towers: TowerInstance[] = []
  projectiles: ProjectileInstance[] = []
  elapsedTime = 0
  defeatedEnemies = 0
  paused = false
  simulationSpeed: SimulationSpeed = 1
  /** Zero-based index of the active or next wave. */
  currentWaveIndex = 0
  wavesCompleted = 0

  /** Canonical routes for this match (ground lanes first, then air). */
  readonly routes: RouteDefinition[]
  /** Track per route id — built once, never rebuilt per frame. */
  readonly routeTracks: Map<string, PathTrack>

  /** Alias of the ground-a track for legacy call sites. */
  private readonly groundTrack: PathTrack
  /** Alias of the air-a track for legacy call sites (null when map has no air). */
  private readonly airTrack: PathTrack | null
  private readonly enemyCatalog: Record<string, EnemyDefinition>
  private readonly towerCatalog: Record<string, TowerDefinition>
  private groupIndex = 0
  private spawnedInGroup = 0
  private spawnTimer = 0
  private spawningComplete = false
  private waveRewardGranted = false
  private waveEnemyTotal = 0
  private waveSpawned = 0
  private lastWaveReward = 0
  private nextEnemyId = 1
  private nextTowerId = 1
  private nextProjectileId = 1
  private combatFx: CombatFx[] = []
  /** Aggregated DoT floaters drained each update slice. */
  private pendingDotFx = new Map<
    string,
    { x: number; y: number; amount: number; shield: boolean }
  >()
  /** Throttle IMMUNE/RESISTED popups per enemy (sim seconds). */
  private statusRejectCooldown = new Map<string, number>()
  /** Performance records keyed by tower id — survive sell until match ends. */
  private towerPerformance = new Map<string, TowerPerformanceRecord>()
  private totalTowerInvestment = 0
  private totalSellRefunds = 0
  /** Cached resolveTowerStats results — filled on demand, never per-frame rebuild of all. */
  private readonly resolvedCombatCache = new Map<string, TowerLevelDefinition>()
  /** Prep countdown for the upcoming wave (READY only). */
  private prepDuration = 0
  private prepElapsed = 0
  private earlyStartBonusGranted = false

  constructor(map: MapDefinition, options: GameSessionOptions = {}) {
    this.map = map
    this.grid = new Grid(map)
    this.gold = map.startingGold
    this.lives = map.startingLives
    this.routes = normalizeMapRoutes(map)
    this.routeTracks = new Map(
      this.routes.map((route) => [route.id, routeTrack(route)] as const)
    )
    const groundId = defaultRouteId(this.routes, 'GROUND')
    const airId = defaultRouteId(this.routes, 'AIR')
    this.groundTrack =
      (groundId ? this.routeTracks.get(groundId) : undefined) ??
      routeTrack({
        id: 'ground-a',
        movementClass: 'GROUND',
        spawn: map.spawn,
        exit: map.exit,
        cells: map.path ?? []
      })
    this.airTrack = (airId ? this.routeTracks.get(airId) : undefined) ?? null
    this.enemyCatalog = options.enemies ?? ENEMY_CATALOG
    this.towerCatalog = options.towers ?? TOWER_CATALOG
    this.campaignContext = options.campaignContext ?? null
    this.researchSnapshot = sanitizeResearchState(
      options.researchSnapshot ?? emptyResearchState()
    )
    this.wavePacing = { ...DEFAULT_WAVE_PACING, ...options.wavePacing }
    this.difficultyPolicy = options.difficultyPolicy ?? {
      hp: 1,
      shield: 1,
      armorValue: 1,
      regen: 1,
      leakDamage: 1,
      speed: 1,
      killReward: 1,
      waveReward: 1,
      startingCoins: map.startingGold
    }
    this.beginPrepCountdown()
  }

  private isPrepCountdownActive(): boolean {
    return (
      this.state === 'READY' &&
      this.currentWaveIndex < this.map.waves.length &&
      this.prepDuration > 0 &&
      this.prepElapsed < this.prepDuration - 1e-9
    )
  }

  private prepRemaining(): number {
    return prepRemainingSeconds(this.prepDuration, this.prepElapsed)
  }

  private beginPrepCountdown(): void {
    if (this.currentWaveIndex >= this.map.waves.length) {
      this.prepDuration = 0
      this.prepElapsed = 0
      this.earlyStartBonusGranted = false
      return
    }
    this.prepDuration =
      this.wavesCompleted === 0
        ? this.wavePacing.firstWavePrepSeconds
        : this.wavePacing.betweenWavesPrepSeconds
    this.prepElapsed = 0
    this.earlyStartBonusGranted = false
  }

  private clearPrepCountdown(): void {
    this.prepElapsed = this.prepDuration
  }

  private grantEarlyStartBonusIfEligible(): void {
    if (this.earlyStartBonusGranted) return
    if (!this.isPrepCountdownActive()) return
    const bonus = computeEarlyStartBonus(
      this.prepRemaining(),
      this.wavePacing.earlyStartCoinsPerSecond
    )
    if (bonus > 0) {
      this.gold += bonus
    }
    this.earlyStartBonusGranted = true
  }

  /**
   * Resolve combat level stats through ResearchModifiers (cached per definition+level).
   * Costs stay authored (Research does not change build/upgrade Coin costs).
   */
  combatLevel(definitionId: string, level: number): TowerLevelDefinition | undefined {
    const definition = this.towerCatalog[definitionId]
    if (!definition) return undefined
    if (!Number.isFinite(level) || level < 1 || level > definition.levels.length) return undefined
    const key = `${definitionId}:${level}`
    const cached = this.resolvedCombatCache.get(key)
    if (cached) return cached

    const v2 = TOWER_DEFINITIONS_V2[definitionId as ResearchTowerId]
    const authored = definition.levels[level - 1]!
    if (!v2 || !isResearchTowerId(definitionId)) {
      this.resolvedCombatCache.set(key, authored)
      return authored
    }
    const ranks = this.researchSnapshot.towers[definitionId]
    const research = researchModifiersForTower(definitionId, ranks)
    const resolved = resolveTowerStats({
      definition: v2,
      towerLevel: level,
      research
    })
    const combat: TowerLevelDefinition = {
      cost: authored.cost,
      range: resolved.range,
      damage: resolved.damage,
      attackInterval: resolved.attackInterval,
      projectileSpeed: resolved.projectileSpeed,
      splashRadius: resolved.splashRadius,
      combatEnabled: resolved.combatEnabled,
      effects: resolved.effects.map((e) => ({ ...e })),
      multiLockTargets: resolved.multiLockTargets
    }
    this.resolvedCombatCache.set(key, combat)
    return combat
  }

  /** Range cells for UI rings / placement preview (Research-aware). */
  combatRange(definitionId: string, level = 1): number {
    return this.combatLevel(definitionId, level)?.range ?? 0
  }

  /** Canonical routes of this match (defensive copy). */
  listRoutes(): RouteDefinition[] {
    return this.routes.map((route) => ({
      ...route,
      spawn: { ...route.spawn },
      exit: { ...route.exit },
      cells: route.cells.map((cell) => ({ ...cell }))
    }))
  }

  /** Track length of a route id — 0 when unknown. */
  routeLength(routeId: string): number {
    return this.routeTracks.get(routeId)?.totalLength ?? 0
  }

  /** Track used by an enemy instance (same movement class only — no cross-class). */
  private trackFor(enemy: EnemyInstance): PathTrack {
    if (enemy.routeId) {
      const track = this.routeTracks.get(enemy.routeId)
      if (track) return track
    }
    const key = enemyRouteKey(enemy, this.enemyCatalog)
    if (key === 'air' && this.airTrack) return this.airTrack
    return this.groundTrack
  }

  /** Exit cell of the route the enemy was assigned to. */
  private exitFor(enemy: EnemyInstance): { x: number; y: number } {
    if (enemy.routeId) {
      const route = findRoute(this.routes, enemy.routeId)
      if (route) return route.exit
    }
    const key = enemyRouteKey(enemy, this.enemyCatalog)
    const fallbackId = defaultRouteId(this.routes, key === 'air' ? 'AIR' : 'GROUND')
    const route = fallbackId ? findRoute(this.routes, fallbackId) : undefined
    return route?.exit ?? this.map.exit
  }

  /**
   * Route an enemy spawns on.
   *
   * Explicit routeId wins when the route exists and is allowed for the enemy's
   * movement class:
   * - GROUND enemies → GROUND routes only
   * - AIR enemies → AIR routes (DEV/special) OR GROUND routes (campaign
   *   physical-path navigation)
   *
   * When omitted: AIR prefers a dedicated air lane if present, otherwise the
   * primary ground lane (campaign-safe). GROUND uses the primary ground lane.
   * Missing lanes return null — no silent inventing of tracks.
   */
  private resolveSpawnRoute(
    movementClass: 'GROUND' | 'AIR',
    requestedRouteId: string | undefined
  ): RouteDefinition | null {
    if (requestedRouteId) {
      const requested = findRoute(this.routes, requestedRouteId)
      if (requested) {
        if (movementClass === 'GROUND' && requested.movementClass === 'GROUND') {
          return requested
        }
        if (movementClass === 'AIR') {
          // Physical path (campaign) or dedicated air corridor (DEV/special).
          return requested
        }
      }
    }
    if (movementClass === 'AIR') {
      const airPreferred = defaultRouteId(this.routes, 'AIR')
      if (airPreferred) return findRoute(this.routes, airPreferred) ?? null
      const groundPreferred = defaultRouteId(this.routes, 'GROUND')
      return groundPreferred ? findRoute(this.routes, groundPreferred) ?? null : null
    }
    const preferred = defaultRouteId(this.routes, 'GROUND')
    return preferred ? findRoute(this.routes, preferred) ?? null : null
  }

  private isBoardActive(): boolean {
    return this.state === 'READY' || this.state === 'PLAYING'
  }

  private currentWave(): WaveDefinition | undefined {
    return this.map.waves[this.currentWaveIndex]
  }

  private currentGroup(): WaveGroupDefinition | undefined {
    return this.currentWave()?.groups[this.groupIndex]
  }

  /** 1-based wave number for UI. */
  displayWaveNumber(): number {
    const total = this.map.waves.length
    if (total <= 0) return 0
    if (this.state === 'VICTORY') return total
    return Math.min(this.currentWaveIndex + 1, total)
  }

  setPaused(paused: boolean): void {
    if (this.state === 'VICTORY' || this.state === 'DEFEAT') return
    this.paused = paused
  }

  setSimulationSpeed(speed: SimulationSpeed): void {
    this.simulationSpeed = speed
  }

  getTowerDefinition(definitionId: string): TowerDefinition | undefined {
    return this.towerCatalog[definitionId]
  }

  getEnemyDefinition(definitionId: string): EnemyDefinition | undefined {
    return this.enemyCatalog[definitionId]
  }

  /** Towers available this match (respects stage allow-list). */
  listAvailableTowers(): TowerDefinition[] {
    return TOWER_TRAY_ORDER.filter((tower) => Boolean(this.towerCatalog[tower.id]))
  }

  /** Composition snapshot for the wave at index (or current/next when omitted). */
  getWaveSummary(index?: number): WaveSummary | null {
    const waveIndex = index ?? this.currentWaveIndex
    const wave = this.map.waves[waveIndex]
    if (!wave) return null
    return summarizeWave(wave, waveIndex)
  }

  canAfford(definitionId: string): boolean {
    const definition = this.towerCatalog[definitionId]
    if (!definition) return false
    return this.gold >= levelCost(definition, 1)
  }

  canStartWave(): boolean {
    return (
      this.state === 'READY' &&
      !this.paused &&
      this.currentWaveIndex < this.map.waves.length &&
      this.isPrepCountdownActive()
    )
  }

  startWave(options?: { early?: boolean }): StartWaveResult {
    if (this.state === 'VICTORY' || this.state === 'DEFEAT') {
      return { ok: false, reason: 'MATCH_OVER' }
    }
    if (this.state !== 'READY') return { ok: false, reason: 'NOT_READY' }
    if (this.paused) return { ok: false, reason: 'PAUSED' }
    if (this.currentWaveIndex >= this.map.waves.length) {
      return { ok: false, reason: 'NO_WAVES' }
    }
    const wave = this.currentWave()
    if (!wave || wave.groups.length === 0) return { ok: false, reason: 'NO_WAVES' }

    if (options?.early) {
      this.grantEarlyStartBonusIfEligible()
    }
    this.clearPrepCountdown()

    this.state = 'PLAYING'
    this.groupIndex = 0
    this.spawnedInGroup = 0
    this.spawningComplete = false
    this.waveRewardGranted = false
    this.lastWaveReward = 0
    this.waveEnemyTotal = wave.groups.reduce((sum, group) => sum + group.count, 0)
    this.waveSpawned = 0
    this.spawnTimer = wave.groups[0].delay
    return { ok: true, waveIndex: this.currentWaveIndex }
  }

  /** Drain and clear visual-only combat feedback for the renderer. */
  consumeCombatFx(): CombatFx[] {
    const fx = this.combatFx
    this.combatFx = []
    return fx
  }

  private emitFx(fx: CombatFx): void {
    pushCombatFx(this.combatFx, fx)
  }

  private flushDotFx(): void {
    for (const [, pending] of this.pendingDotFx) {
      if (pending.amount < 0.5) continue
      this.emitFx({
        type: 'damageNumber',
        x: pending.x,
        y: pending.y,
        amount: Math.round(pending.amount * 10) / 10,
        style: pending.shield ? 'shield' : 'dot'
      })
    }
    this.pendingDotFx.clear()
  }

  waveDebug(): WaveDebugSnapshot {
    return {
      state: this.state,
      waveIndex: this.currentWaveIndex,
      groupIndex: this.groupIndex,
      spawnedInGroup: this.spawnedInGroup,
      spawningComplete: this.spawningComplete,
      spawnTimer: this.spawnTimer,
      livingEnemies: this.enemies.filter((enemy) => enemy.alive).length,
      waveRewardGranted: this.waveRewardGranted
    }
  }

  previewPlacement(definitionId: string, anchor: GridPosition): PlaceTowerResult {
    if (!this.isBoardActive()) return { ok: false, reason: 'NOT_ACTIVE' }
    const definition = this.towerCatalog[definitionId]
    if (!definition) return { ok: false, reason: 'UNKNOWN_TOWER' }
    if (this.gold < levelCost(definition, 1)) return { ok: false, reason: 'INSUFFICIENT_GOLD' }
    const check = this.grid.canPlaceFootprint(anchor, definition.footprint)
    if (!check.ok) return { ok: false, reason: check.reason }
    return {
      ok: true,
      tower: {
        id: 'preview',
        definitionId,
        anchor: { ...anchor },
        level: 1,
        totalInvested: levelCost(definition, 1),
        occupiedCells: check.cells,
        cooldownRemaining: 0,
        targetingMode: 'FIRST'
      }
    }
  }

  placeTower(definitionId: string, anchor: GridPosition): PlaceTowerResult {
    if (!this.isBoardActive()) return { ok: false, reason: 'NOT_ACTIVE' }
    if (this.paused) return { ok: false, reason: 'PAUSED' }

    const definition = this.towerCatalog[definitionId]
    if (!definition) return { ok: false, reason: 'UNKNOWN_TOWER' }

    const cost = levelCost(definition, 1)
    if (this.gold < cost) return { ok: false, reason: 'INSUFFICIENT_GOLD' }

    const check = this.grid.canPlaceFootprint(anchor, definition.footprint)
    if (!check.ok) return { ok: false, reason: check.reason }

    this.gold -= cost
    const tower: TowerInstance = {
      id: `tower-${this.nextTowerId}`,
      definitionId,
      anchor: { ...anchor },
      level: 1,
      totalInvested: cost,
      occupiedCells: check.cells.map((cell) => ({ ...cell })),
      cooldownRemaining: 0,
      targetingMode: 'FIRST'
    }
    this.nextTowerId += 1
    this.grid.occupy(tower.id, tower.occupiedCells)
    this.towers.push(tower)
    this.towerPerformance.set(
      tower.id,
      createTowerPerformance(tower.id, definitionId, cost)
    )
    this.totalTowerInvestment += cost
    return { ok: true, tower }
  }

  towerIdAt(position: GridPosition): string | null {
    return this.grid.towerIdAt(position)
  }

  getTower(towerId: string): TowerInstance | undefined {
    return this.towers.find((tower) => tower.id === towerId)
  }

  getTowerSnapshot(towerId: string): TowerInspectSnapshot | null {
    const tower = this.getTower(towerId)
    if (!tower) return null
    const definition = this.towerCatalog[tower.definitionId]
    if (!definition) return null
    const current = this.combatLevel(tower.definitionId, tower.level)
    if (!current) return null
    const cap = maxLevel(definition)
    const next =
      tower.level < cap ? this.combatLevel(tower.definitionId, tower.level + 1) : undefined
    const nextCost =
      tower.level < cap ? definition.levels[tower.level]?.cost : undefined
    const perf = this.towerPerformance.get(towerId)
    return {
      id: tower.id,
      definitionId: tower.definitionId,
      name: definition.name,
      letter: definition.letter,
      color: definition.color,
      level: tower.level,
      maxLevel: cap,
      totalInvested: tower.totalInvested,
      damage: current.damage,
      range: current.range,
      attackInterval: current.attackInterval,
      projectileSpeed: current.projectileSpeed,
      splashRadius: current.splashRadius,
      effects: current.effects.map((effect) => ({ ...effect })),
      targetingMode: tower.targetingMode,
      sellRefund: sellRefund(tower.totalInvested),
      nextUpgrade: next
        ? {
            cost: nextCost ?? next.cost,
            damage: next.damage,
            range: next.range,
            attackInterval: next.attackInterval,
            splashRadius: next.splashRadius,
            effects: next.effects.map((effect) => ({ ...effect }))
          }
        : null,
      canUpgrade: Boolean(next),
      canAffordUpgrade: nextCost !== undefined && this.gold >= nextCost,
      damageDealt: perf?.damageDealt ?? 0,
      kills: perf?.kills ?? 0,
      shotsFired: perf?.shotsFired ?? 0,
      hits: perf?.hits ?? 0,
      splashHits: perf?.splashHits ?? 0,
      statusApplications: perf?.statusApplications ?? 0,
      armorBreakApplications: perf?.armorBreakApplications ?? 0,
      armorBreakUptime: perf?.armorBreakUptime ?? 0,
      damageAmplifiedByArmorBreak: perf?.damageAmplifiedByArmorBreak ?? 0
    }
  }

  getTowerPerformance(towerId: string): TowerPerformanceRecord | undefined {
    return this.towerPerformance.get(towerId)
  }

  listTowerPerformance(): TowerPerformanceRecord[] {
    return [...this.towerPerformance.values()]
  }

  upgradeTower(towerId: string): UpgradeTowerResult {
    if (!this.isBoardActive()) return { ok: false, reason: 'NOT_ACTIVE' }
    const tower = this.getTower(towerId)
    if (!tower) return { ok: false, reason: 'UNKNOWN_TOWER' }
    const definition = this.towerCatalog[tower.definitionId]
    if (!definition) return { ok: false, reason: 'UNKNOWN_TOWER' }

    const cap = maxLevel(definition)
    if (tower.level >= cap) return { ok: false, reason: 'MAX_LEVEL' }

    const current = this.combatLevel(tower.definitionId, tower.level)
    const nextCombat = this.combatLevel(tower.definitionId, tower.level + 1)
    const nextAuthored = definition.levels[tower.level]
    if (!current || !nextCombat || !nextAuthored) return { ok: false, reason: 'MAX_LEVEL' }

    if (this.gold < nextAuthored.cost) return { ok: false, reason: 'INSUFFICIENT_GOLD' }

    this.gold -= nextAuthored.cost
    tower.totalInvested += nextAuthored.cost
    tower.level += 1
    this.totalTowerInvestment += nextAuthored.cost
    const perf = this.towerPerformance.get(tower.id)
    if (perf) perf.totalInvested = tower.totalInvested

    const oldInterval = current.attackInterval
    const newInterval = nextCombat.attackInterval
    if (oldInterval > 1e-9) {
      tower.cooldownRemaining = tower.cooldownRemaining * (newInterval / oldInterval)
    } else {
      tower.cooldownRemaining = Math.min(tower.cooldownRemaining, newInterval)
    }

    this.emitFx({ type: 'towerPulse', towerId: tower.id, kind: 'upgrade' })
    return { ok: true, tower, cost: nextAuthored.cost }
  }

  sellTower(towerId: string): SellTowerResult {
    if (!this.isBoardActive()) return { ok: false, reason: 'NOT_ACTIVE' }
    const tower = this.getTower(towerId)
    if (!tower) return { ok: false, reason: 'UNKNOWN_TOWER' }

    const refund = sellRefund(tower.totalInvested)
    this.gold += refund
    this.totalSellRefunds += refund
    this.emitFx({ type: 'towerPulse', towerId: tower.id, kind: 'sell' })
    if (refund > 0) {
      const center = footprintCenter(
        tower.anchor,
        this.towerCatalog[tower.definitionId]?.footprint ?? { width: 2, height: 2 }
      )
      this.emitFx({ type: 'goldPopup', x: center.x, y: center.y, amount: refund })
    }
    this.grid.release(tower.id)
    this.towers = this.towers.filter((entry) => entry.id !== towerId)
    const perf = this.towerPerformance.get(towerId)
    if (perf) perf.sold = true
    return { ok: true, refund }
  }

  setTowerTargeting(towerId: string, mode: string): SetTargetingResult {
    if (!this.isBoardActive()) return { ok: false, reason: 'NOT_ACTIVE' }
    if (!isTargetingMode(mode)) return { ok: false, reason: 'INVALID_MODE' }
    const tower = this.getTower(towerId)
    if (!tower) return { ok: false, reason: 'UNKNOWN_TOWER' }
    tower.targetingMode = mode
    return { ok: true, tower, targetingMode: mode }
  }

  view(): SessionView {
    const waveNumber = this.displayWaveNumber()
    const waveCount = this.map.waves.length
    const alive = this.enemies.filter((enemy) => enemy.alive).length
    const unresolved =
      this.state === 'PLAYING'
        ? Math.max(0, this.waveEnemyTotal - this.waveSpawned) + alive
        : 0
    const prepRemaining = this.isPrepCountdownActive() ? this.prepRemaining() : 0
    const isPrepCountdown = prepRemaining > 0
    return {
      lives: this.lives,
      gold: this.gold,
      state: this.state,
      elapsedTime: this.elapsedTime,
      defeatedEnemies: this.defeatedEnemies,
      paused: this.paused,
      simulationSpeed: this.simulationSpeed,
      waveNumber,
      waveCount,
      wavesCompleted: this.wavesCompleted,
      waveLabel: waveCount > 0 ? `Wave ${waveNumber} / ${waveCount}` : 'Wave —',
      canStartWave: this.canStartWave(),
      isPrepCountdown,
      prepCountdownRemaining: prepCountdownDisplaySeconds(prepRemaining),
      prepCountdownTotal: isPrepCountdown ? this.prepDuration : 0,
      earlyStartBonusPreview: isPrepCountdown
        ? computeEarlyStartBonus(prepRemaining, this.wavePacing.earlyStartCoinsPerSecond)
        : 0,
      towerCount: this.towers.length,
      wavePreview: this.buildWavePreviewView(),
      waveProgress:
        this.state === 'PLAYING'
          ? {
              total: this.waveEnemyTotal,
              spawned: this.waveSpawned,
              alive,
              unresolved
            }
          : null,
      lastWaveReward: this.lastWaveReward,
      totalDamageDealt: this.sumPerformance((p) => p.damageDealt),
      totalShotsFired: this.sumPerformance((p) => p.shotsFired),
      totalTowerInvestment: this.totalTowerInvestment,
      totalSellRefunds: this.totalSellRefunds,
      campaign: this.campaignContext
        ? {
            campaignId: this.campaignContext.campaignId,
            chapterId: this.campaignContext.chapterId,
            stageId: this.campaignContext.stageId,
            difficultyId: this.campaignContext.difficultyId,
            mapId: this.campaignContext.mapId,
            displayLabel: this.campaignContext.displayLabel
          }
        : null,
      research: this.researchSnapshot
    }
  }

  private sumPerformance(pick: (record: TowerPerformanceRecord) => number): number {
    let total = 0
    for (const record of this.towerPerformance.values()) {
      total += pick(record)
    }
    return total
  }

  private buildWavePreviewView(): WavePreviewView | null {
    if (this.state === 'VICTORY' || this.state === 'DEFEAT') return null
    const index = this.currentWaveIndex
    if (index < 0 || index >= this.map.waves.length) return null
    const summary = this.getWaveSummary(index)
    if (!summary) return null
    const wave = this.map.waves[index]
    const multiLane = this.mapHasMultipleGroundRoutes()
    /** Aggregate by family + level + rank + lane so intel can show "GRUNT Lv3 ×8 · A". */
    const bucket = new Map<
      string,
      {
        enemyId: string
        enemyLevel: number
        enemyRank: string | undefined
        count: number
        routeId: string | undefined
      }
    >()
    for (const group of wave?.groups ?? []) {
      const enemyId = group.enemy
      const enemyLevel = Math.max(1, group.enemyLevel ?? 1)
      const enemyRank = group.enemyRank
      const rankKey = enemyRank ?? 'NORMAL'
      const routeId = group.routeId
      const chipKey = multiLane
        ? `${enemyId}|${enemyLevel}|${rankKey}|${routeId ?? ''}`
        : `${enemyId}|${enemyLevel}|${rankKey}`
      const prev = bucket.get(chipKey)
      if (prev) {
        prev.count += group.count
      } else {
        bucket.set(chipKey, {
          enemyId,
          enemyLevel,
          enemyRank: enemyRank === 'NORMAL' ? undefined : enemyRank,
          count: group.count,
          routeId
        })
      }
    }
    const chips: WavePreviewChip[] = []
    const trayOrder = ENEMY_TRAY_ORDER.map((d) => d.id)
    const sorted = [...bucket.entries()].sort((a, b) => {
      const ia = trayOrder.indexOf(a[1].enemyId)
      const ib = trayOrder.indexOf(b[1].enemyId)
      const oa = ia < 0 ? 999 : ia
      const ob = ib < 0 ? 999 : ib
      if (oa !== ob) return oa - ob
      if (a[1].enemyLevel !== b[1].enemyLevel) return a[1].enemyLevel - b[1].enemyLevel
      return (a[1].enemyRank ?? '').localeCompare(b[1].enemyRank ?? '')
    })
    for (const [chipKey, entry] of sorted) {
      if (entry.count <= 0) continue
      const live = this.enemyCatalog[entry.enemyId]
      const tray = ENEMY_TRAY_ORDER.find((d) => d.id === entry.enemyId)
      const movement = enemyMovementClass(live ?? tray)
      chips.push({
        id: entry.enemyId,
        chipKey,
        name: live?.name ?? tray?.name ?? entry.enemyId,
        count: entry.count,
        color: live?.color ?? tray?.color ?? 0xef4444,
        enemyLevel: entry.enemyLevel,
        movementClass: movement,
        enemyRank: entry.enemyRank,
        laneLabel: multiLane ? laneLabelForGroup(entry.routeId, movement) : undefined
      })
    }
    return {
      waveNumber: index + 1,
      waveCount: this.map.waves.length,
      totalEnemies: summary.composition.total,
      reward: scaleReward(summary.reward, this.difficultyPolicy.waveReward),
      chips
    }
  }

  private mapHasMultipleGroundRoutes(): boolean {
    const routes = this.map.routes
    if (!routes || routes.length === 0) {
      // Fallback: inspect wave routeIds
      const ids = new Set<string>()
      for (const wave of this.map.waves) {
        for (const g of wave.groups) {
          if (g.routeId && !g.routeId.startsWith('air')) ids.add(g.routeId)
        }
      }
      return ids.size >= 2
    }
    return routes.filter((r) => r.movementClass === 'GROUND').length >= 2
  }

  update(dt: number): void {
    if (!Number.isFinite(dt) || dt <= 0) return
    if (this.state === 'VICTORY' || this.state === 'DEFEAT') return

    let combatDt = dt

    if (this.state === 'READY' && !this.paused && this.isPrepCountdownActive()) {
      const prepLeft = this.prepDuration - this.prepElapsed
      const prepSlice = Math.min(combatDt, prepLeft)
      this.prepElapsed += prepSlice
      combatDt -= prepSlice
      if (this.prepElapsed >= this.prepDuration - 1e-9) {
        this.prepElapsed = this.prepDuration
        const started = this.startWave()
        if (!started.ok) return
      } else {
        return
      }
    }

    if (this.state !== 'PLAYING' || this.paused) return
    if (combatDt <= 0) return

    let remaining = combatDt * this.simulationSpeed
    while (remaining > 0 && this.state === 'PLAYING') {
      this.spawnDue()
      if (this.state !== 'PLAYING') break

      const untilSpawn = this.spawningComplete
        ? Number.POSITIVE_INFINITY
        : Math.max(0, this.spawnTimer)
      const slice =
        untilSpawn === Number.POSITIVE_INFINITY
          ? remaining
          : Math.min(remaining, untilSpawn > 0 ? untilSpawn : remaining)
      if (slice <= 0) break

      for (const enemy of this.enemies) {
        enemy.regenDamagedThisSlice = false
      }

      this.elapsedTime += slice
      if (!this.spawningComplete) {
        this.spawnTimer = Math.max(0, this.spawnTimer - slice)
      }
      this.move(slice)
      if (this.state !== 'PLAYING') break
      this.updateTowers(slice)
      this.updateProjectiles(slice)
      this.updateStatusEffects(slice)
      this.updateRegen(slice)
      this.flushDotFx()
      this.pruneDead()
      this.tryCompleteWave()
      remaining -= slice
    }
  }

  applyDamage(
    enemyId: string,
    amount: number,
    source?: DamageSource & { damageType?: DamageType }
  ): ApplyDamageResult {
    if (this.state === 'DEFEAT' || this.state === 'VICTORY') {
      return { dealt: 0, killed: false }
    }
    if (amount <= 0) return { dealt: 0, killed: false }
    const enemy = this.enemies.find((entry) => entry.id === enemyId)
    if (!enemy || !enemy.alive) return { dealt: 0, killed: false }

    const definition = this.enemyCatalog[enemy.definitionId]
    const damageType = source?.damageType ?? 'ENERGY'
    const typeMultiplier = resolveTypeMultiplier(
      damageType,
      definition?.damageModifiers ?? definition?.armorProfile?.damageModifiers
    )
    const baseArmor =
      (definition?.armorProfile?.armorValue ?? 0) * this.difficultyPolicy.armorValue
    const armorValue = effectiveArmorValue(baseArmor, enemy)
    const mitigated = resolveDamage({
      rawDamage: amount,
      damageType,
      typeMultiplier,
      armorValue
    })
    const unbroken =
      armorValue < baseArmor
        ? resolveDamage({
            rawDamage: amount,
            damageType,
            typeMultiplier,
            armorValue: baseArmor
          })
        : null
    // Shield-before-HP ordering preserved exactly (post type/armor mitigation).
    const incoming = mitigated.finalDamage
    if (incoming <= 0) return { dealt: 0, killed: false }

    const durability = (enemy.shield ?? 0) + enemy.health
    const dealt = Math.min(incoming, durability)
    if (dealt <= 0) return { dealt: 0, killed: false }

    if (unbroken && unbroken.finalDamage < incoming) {
      const ampRaw = incoming - unbroken.finalDamage
      const ampAttributed = dealt * (ampRaw / incoming)
      const breakerId = findStatus(enemy, 'armorBreak')?.sourceTowerId
      if (breakerId && ampAttributed > 0) {
        const breakerPerf = this.towerPerformance.get(breakerId)
        if (breakerPerf) breakerPerf.damageAmplifiedByArmorBreak += ampAttributed
      }
    }

    if (definition?.regen) {
      enemy.regenDelayRemaining = definition.regen.delayAfterDamage
      enemy.regenDamagedThisSlice = true
    }

    let remaining = dealt
    const shieldBefore = enemy.shield ?? 0
    let shieldAbsorbed = 0
    if (shieldBefore > 0) {
      const absorbed = Math.min(shieldBefore, remaining)
      enemy.shield = shieldBefore - absorbed
      remaining -= absorbed
      shieldAbsorbed = absorbed
    }
    if (remaining > 0) {
      enemy.health = Math.max(0, enemy.health - remaining)
    }

    if (shieldBefore > 0 && (enemy.shield ?? 0) <= 0) {
      this.emitFx({
        type: 'shieldBreak',
        x: enemy.position.x,
        y: enemy.position.y,
        enemyId: enemy.id
      })
    }

    const killed = enemy.health <= 0
    if (source?.towerId) {
      const perf = this.towerPerformance.get(source.towerId)
      if (perf) {
        perf.damageDealt += dealt
        if (killed) perf.kills += 1
      }
    }

    if (source?.kind === 'dot') {
      const key = `${enemy.id}:${shieldAbsorbed > 0 && remaining <= 0 ? 's' : 'h'}`
      const existing = this.pendingDotFx.get(key)
      if (existing) {
        existing.amount += dealt
        existing.x = enemy.position.x
        existing.y = enemy.position.y
      } else {
        this.pendingDotFx.set(key, {
          x: enemy.position.x,
          y: enemy.position.y,
          amount: dealt,
          shield: shieldAbsorbed > 0 && remaining <= 0
        })
      }
    } else if (dealt > 0) {
      const amplified =
        unbroken !== null &&
        unbroken.finalDamage < incoming &&
        shieldAbsorbed < dealt
      this.emitFx({
        type: 'damageNumber',
        x: enemy.position.x,
        y: enemy.position.y,
        amount: Math.round(dealt),
        style: shieldAbsorbed >= dealt
          ? 'shield'
          : amplified
            ? 'amplified'
            : source?.kind === 'splash'
              ? 'splash'
              : 'direct'
      })
      this.emitFx({
        type: 'hitFlash',
        enemyId: enemy.id,
        shield: shieldAbsorbed > 0
      })
    }

    if (killed) {
      this.resolveKill(enemy)
    }
    return { dealt, killed }
  }
  private resolveKill(enemy: EnemyInstance): void {
    if (this.state === 'DEFEAT' || this.state === 'VICTORY') return
    if (!enemy.alive) return

    const poison = findStatus(enemy, 'poison')
    const shouldContagion = poison?.contagious === true
    const contagionSpec: TowerEffectSpec | undefined = shouldContagion
      ? {
          id: 'poison',
          duration: 5.5,
          slow: poison!.slow,
          damagePerSecond: poison!.damagePerSecond,
          contagious: true
        }
      : undefined
    const contagionSource = poison?.sourceTowerId
    const deathPosition = { ...enemy.position }

    enemy.alive = false
    enemy.health = 0
    this.defeatedEnemies += 1
    const definition = this.enemyCatalog[enemy.definitionId]
    const reward = enemy.killReward ?? definition?.reward ?? 0
    this.gold += reward
    this.emitFx({
      type: 'death',
      x: deathPosition.x,
      y: deathPosition.y,
      color: definition?.color,
      heavy:
        enemy.definitionId === 'boss' ||
        enemy.definitionId === 'air_boss' ||
        enemy.enemyRank === 'BOSS'
    })
    if (reward > 0) {
      this.emitFx({
        type: 'goldPopup',
        x: deathPosition.x,
        y: deathPosition.y,
        amount: reward
      })
    }

    if (contagionSpec) {
      this.applyContagion(enemy.id, deathPosition, contagionSpec, contagionSource)
    }
    enemy.statusEffects = []
  }

  /**
   * Spread LV4 Poison from a dead contagious carrier.
   * Prefer unpoisoned, then greatest pathProgress, then lowest id. Max 2.
   */
  private applyContagion(
    deadEnemyId: string,
    origin: { x: number; y: number },
    spec: TowerEffectSpec,
    sourceTowerId?: string
  ): void {
    const candidates = this.enemies.filter((enemy) => {
      if (!enemy.alive || enemy.id === deadEnemyId) return false
      return distanceCells(origin.x, origin.y, enemy.position.x, enemy.position.y) <= CONTAGION_RADIUS
    })
    candidates.sort((a, b) => {
      const aPoisoned = hasStatus(a, 'poison') ? 1 : 0
      const bPoisoned = hasStatus(b, 'poison') ? 1 : 0
      if (aPoisoned !== bPoisoned) return aPoisoned - bPoisoned
      if (b.pathProgress !== a.pathProgress) return b.pathProgress - a.pathProgress
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    })
    const targets = candidates.slice(0, CONTAGION_MAX_TARGETS)
    for (const target of targets) {
      applyStatus(target, spec, sourceTowerId, statusTraitsFromDefinition(this.enemyCatalog[target.definitionId]))
      if (sourceTowerId) {
        const perf = this.towerPerformance.get(sourceTowerId)
        if (perf) perf.statusApplications += 1
      }
      this.emitFx({
        type: 'contagion',
        fromX: origin.x,
        fromY: origin.y,
        toX: target.position.x,
        toY: target.position.y
      })
    }
  }

  private pruneDead(): void {
    this.enemies = this.enemies.filter((enemy) => {
      if (enemy.alive) return true
      this.statusRejectCooldown.delete(enemy.id)
      return false
    })
  }

  private tryCompleteWave(): void {
    if (this.state !== 'PLAYING') return
    if (!this.spawningComplete) return
    if (this.enemies.some((enemy) => enemy.alive)) return
    if (this.waveRewardGranted) return

    const wave = this.currentWave()
    this.waveRewardGranted = true
    const rawReward = wave?.completionReward ?? 0
    const reward = scaleReward(rawReward, this.difficultyPolicy.waveReward)
    this.lastWaveReward = reward
    this.gold += reward
    this.wavesCompleted += 1
    this.currentWaveIndex += 1
    this.paused = false
    this.projectiles = []
    this.flushDotFx()
    this.pendingDotFx.clear()
    // Keep pending kill/death/gold FX — only append wave-clear cue.
    this.emitFx({ type: 'waveClear', reward })
    this.waveEnemyTotal = 0
    this.waveSpawned = 0

    if (this.currentWaveIndex >= this.map.waves.length) {
      this.state = 'VICTORY'
      this.clearPrepCountdown()
    } else {
      this.state = 'READY'
      this.beginPrepCountdown()
    }
  }

  private spawnDue(): void {
    if (this.spawningComplete || this.state !== 'PLAYING') return

    while (!this.spawningComplete && this.spawnTimer <= 1e-9) {
      const group = this.currentGroup()
      if (!group) {
        this.spawningComplete = true
        break
      }

      this.spawnEnemy(group)
      this.spawnedInGroup += 1

      if (this.spawnedInGroup < group.count) {
        this.spawnTimer = group.interval
        if (group.interval <= 1e-9) continue
        break
      }

      this.groupIndex += 1
      this.spawnedInGroup = 0
      const next = this.currentGroup()
      if (!next) {
        this.spawningComplete = true
        this.spawnTimer = Number.POSITIVE_INFINITY
        break
      }
      this.spawnTimer = next.delay
      if (next.delay <= 1e-9) continue
      break
    }
  }

  private spawnEnemy(group: WaveGroupDefinition | string): void {
    const enemyId = typeof group === 'string' ? group : group.enemy
    const definition = this.enemyCatalog[enemyId]
    if (!definition) return
    const enemyLevel =
      typeof group === 'string' ? 1 : Math.max(1, group.enemyLevel ?? 1)
    const enemyRank =
      typeof group === 'string'
        ? definition.defaultEnemyRank ?? (enemyId === 'boss' || enemyId === 'air_boss' ? 'BOSS' : 'NORMAL')
        : group.enemyRank ??
          definition.defaultEnemyRank ??
          (enemyId === 'boss' || enemyId === 'air_boss' ? 'BOSS' : 'NORMAL')
    const movementClass = enemyMovementClass(definition)
    const requestedRouteId = typeof group === 'string' ? undefined : group.routeId
    const route = this.resolveSpawnRoute(movementClass, requestedRouteId)
    // No silent cross-class track: refuse spawn when no matching route exists.
    if (!route) return
    const track = this.routeTracks.get(route.id)
    if (!track) return

    let maxHealth = definition.maxHealth
    let killReward = definition.reward
    let maxShield = definition.shield?.maxShield ?? 0
    const v2 = ENEMY_DEFINITIONS_V2[enemyId]
    const defaultRank =
      definition.defaultEnemyRank ??
      (enemyId === 'boss' || enemyId === 'air_boss' ? 'BOSS' : 'NORMAL')
    if (v2 && (enemyLevel !== 1 || enemyRank !== defaultRank)) {
      const resolved = resolveEnemyStats(v2, { enemyLevel, enemyRank })
      maxHealth = resolved.maxHp
      killReward = resolved.reward
      maxShield = resolved.shield?.maxShield ?? 0
    }

    const policy = this.difficultyPolicy
    maxHealth = Math.max(1, Math.round(maxHealth * policy.hp))
    maxShield = Math.max(0, Math.round(maxShield * policy.shield))
    killReward = scaleReward(killReward, policy.killReward)

    const instance = createEnemyInstance(
      definition,
      `enemy-${this.nextEnemyId}`,
      positionAt(track, 0),
      {
        enemyLevel,
        enemyRank,
        routeId: route.id,
        routeLength: track.totalLength,
        maxHealth,
        killReward
      }
    )
    instance.maxShield = maxShield
    instance.shield = maxShield
    this.enemies.push(instance)
    this.nextEnemyId += 1
    this.waveSpawned += 1
  }

  private updateStatusEffects(dt: number): void {
    for (const enemy of this.enemies) {
      if (!enemy.alive) continue
      const armorBreak = findStatus(enemy, 'armorBreak')
      if (armorBreak?.sourceTowerId) {
        const perf = this.towerPerformance.get(armorBreak.sourceTowerId)
        if (perf) {
          perf.armorBreakUptime += Math.min(dt, Math.max(0, armorBreak.remainingDuration))
        }
      }
      const ticks = tickStatusEffects(enemy, dt)
      for (const tick of ticks) {
        if (tick.amount <= 0) continue
        let damageType: DamageType = 'ENERGY'
        if (tick.sourceTowerId) {
          const tower = this.getTower(tick.sourceTowerId)
          const perf = this.towerPerformance.get(tick.sourceTowerId)
          const definitionId = tower?.definitionId ?? perf?.definitionId
          const definition = definitionId ? this.towerCatalog[definitionId] : undefined
          if (definition) damageType = towerDamageType(definition)
        }
        this.applyDamage(enemy.id, tick.amount, {
          towerId: tick.sourceTowerId,
          kind: 'dot',
          damageType
        })
      }
    }
  }

  private updateRegen(dt: number): void {
    for (const enemy of this.enemies) {
      if (!enemy.alive) continue
      const definition = this.enemyCatalog[enemy.definitionId]
      const regen = definition?.regen
      if (!regen) continue
      const regenRate = regen.hpPerSecond * this.difficultyPolicy.regen

      const damagedThisSlice = enemy.regenDamagedThisSlice === true
      enemy.regenDamagedThisSlice = false

      const delay = enemy.regenDelayRemaining ?? 0
      if (damagedThisSlice) {
        // Damage landed this slice — countdown only; no same-slice heal.
        enemy.regenDelayRemaining = Math.max(0, delay - dt)
        continue
      }

      if (delay > 1e-9) {
        if (dt <= delay) {
          enemy.regenDelayRemaining = Math.max(0, delay - dt)
          continue
        }
        enemy.regenDelayRemaining = 0
        const leftover = dt - delay
        if (leftover > 0 && enemy.health < enemy.maxHealth) {
          const before = enemy.health
          enemy.health = Math.min(
            enemy.maxHealth,
            enemy.health + regenRate * leftover
          )
          const gained = enemy.health - before
          if (gained > 0.01) {
            this.emitFx({
              type: 'regenHeal',
              x: enemy.position.x,
              y: enemy.position.y,
              enemyId: enemy.id,
              amount: gained
            })
          }
        }
        continue
      }

      enemy.regenDelayRemaining = 0
      if (enemy.health >= enemy.maxHealth) continue
      {
        const before = enemy.health
        enemy.health = Math.min(enemy.maxHealth, enemy.health + regenRate * dt)
        const gained = enemy.health - before
        if (gained > 0.01) {
          this.emitFx({
            type: 'regenHeal',
            x: enemy.position.x,
            y: enemy.position.y,
            enemyId: enemy.id,
            amount: gained
          })
        }
      }
    }
  }

  private move(step: number): void {
    const survivors: EnemyInstance[] = []
    for (const enemy of this.enemies) {
      if (this.state !== 'PLAYING') {
        survivors.push(enemy)
        continue
      }
      if (!enemy.alive) continue
      const definition = this.enemyCatalog[enemy.definitionId]
      const baseSpeed = definition?.speed ?? 0
      const speed = effectiveSpeed(baseSpeed, enemy)
      const track = this.trackFor(enemy)
      enemy.pathProgress += speed * step
      if (track.totalLength <= 0 || enemy.pathProgress >= track.totalLength) {
        const rawLeak = definition?.leakDamage ?? 0
        const leak =
          rawLeak <= 0
            ? 0
            : Math.max(1, Math.round(rawLeak * this.difficultyPolicy.leakDamage))
        this.lives -= leak
        enemy.alive = false
        const exit = this.exitFor(enemy)
        this.emitFx({
          type: 'leak',
          x: exit.x + 0.5,
          y: exit.y + 0.5,
          amount: leak
        })
        if (this.lives <= 0) {
          this.lives = 0
          this.state = 'DEFEAT'
          this.paused = false
        }
        continue
      }
      enemy.position = positionAt(track, enemy.pathProgress)
      survivors.push(enemy)
    }
    this.enemies = survivors
  }

  private updateTowers(dt: number): void {
    for (const tower of this.towers) {
      const definition = this.towerCatalog[tower.definitionId]
      if (!definition) continue
      const level = this.combatLevel(tower.definitionId, tower.level)
      if (!level || !level.combatEnabled) continue

      tower.cooldownRemaining = Math.max(0, tower.cooldownRemaining - dt)
      if (tower.cooldownRemaining > 0) continue

      const extraLocks = Math.max(0, level.multiLockTargets ?? 0)
      const targets =
        extraLocks > 0
          ? selectMultiLockTargets(tower, definition, this.enemies, extraLocks, level)
          : (() => {
              const one = selectTarget(tower, definition, this.enemies, level)
              return one ? [one] : []
            })()
      if (targets.length === 0) continue

      const center = footprintCenter(tower.anchor, definition.footprint)
      const isCannonLv4 = tower.definitionId === 'cannon' && tower.level >= 4
      for (const target of targets) {
        this.projectiles.push({
          id: `proj-${this.nextProjectileId}`,
          towerId: tower.id,
          definitionId: tower.definitionId,
          x: center.x,
          y: center.y,
          targetEnemyId: target.id,
          speed: level.projectileSpeed,
          damage: level.damage,
          splashRadius: level.splashRadius,
          effects: level.effects.map((effect) => ({ ...effect })),
          alive: true,
          towerLevel: tower.level,
          shockwaveInnerRadius: isCannonLv4 ? SHOCKWAVE_INNER_RADIUS : undefined,
          shockwaveBonusDamage: isCannonLv4 ? SHOCKWAVE_BONUS_DAMAGE : undefined
        })
        this.nextProjectileId += 1
      }
      tower.cooldownRemaining = level.attackInterval
      const perf = this.towerPerformance.get(tower.id)
      if (perf) perf.shotsFired += targets.length
      this.emitFx({
        type: 'towerPulse',
        towerId: tower.id,
        kind: 'fire',
        multiLock: targets.length > 1
      })
    }
  }

  private updateProjectiles(dt: number): void {
    const remaining: ProjectileInstance[] = []
    for (const projectile of this.projectiles) {
      if (!projectile.alive) continue

      const target = this.enemies.find(
        (enemy) => enemy.id === projectile.targetEnemyId && enemy.alive
      )
      if (!target) {
        projectile.alive = false
        continue
      }

      const dx = target.position.x - projectile.x
      const dy = target.position.y - projectile.y
      const dist = Math.hypot(dx, dy)
      const step = projectile.speed * dt

      if (dist <= step || dist < 1e-9) {
        this.impactProjectile(projectile, target.position.x, target.position.y)
        projectile.alive = false
        continue
      }

      const inv = 1 / dist
      projectile.x += dx * inv * step
      projectile.y += dy * inv * step
      remaining.push(projectile)
    }
    this.projectiles = remaining
  }

  private impactProjectile(projectile: ProjectileInstance, impactX: number, impactY: number): void {
    const hitIds = new Set<string>()
    const hasSplash = projectile.splashRadius > 0

    if (hasSplash) {
      for (const enemy of this.enemies) {
        if (!enemy.alive) continue
        if (distanceCells(impactX, impactY, enemy.position.x, enemy.position.y) <= projectile.splashRadius) {
          hitIds.add(enemy.id)
        }
      }
      this.emitFx({
        type: 'splash',
        x: impactX,
        y: impactY,
        radius: projectile.splashRadius,
        color: projectile.definitionId === 'frost' ? 0x38bdf8 : 0x9ca3af,
        innerRadius:
          projectile.shockwaveInnerRadius !== undefined
            ? projectile.shockwaveInnerRadius
            : undefined
      })
    } else {
      hitIds.add(projectile.targetEnemyId)
      const family =
        projectile.definitionId === 'frost' ||
        projectile.definitionId === 'cannon' ||
        projectile.definitionId === 'poison' ||
        projectile.definitionId === 'aa' ||
        projectile.definitionId === 'mage'
          ? projectile.definitionId
          : 'fire'
      this.emitFx({
        type: 'impact',
        x: impactX,
        y: impactY,
        family,
        signature: family === 'fire' && (projectile.towerLevel ?? 0) >= 4
      })
    }

    const perf = this.towerPerformance.get(projectile.towerId)
    let primaryHit = false
    const isFrostLv4 =
      projectile.definitionId === 'frost' && (projectile.towerLevel ?? 0) >= 4

    for (const enemyId of hitIds) {
      const enemyBefore = this.enemies.find((entry) => entry.id === enemyId && entry.alive)
      if (!enemyBefore) continue

      const isPrimary = enemyId === projectile.targetEnemyId
      const dist = distanceCells(impactX, impactY, enemyBefore.position.x, enemyBefore.position.y)
      let damage = projectile.damage
      if (
        projectile.shockwaveBonusDamage &&
        projectile.shockwaveInnerRadius !== undefined &&
        dist <= projectile.shockwaveInnerRadius
      ) {
        damage += projectile.shockwaveBonusDamage
      }

      const hadFrost = hasStatus(enemyBefore, 'frost')
      const towerDef = this.towerCatalog[projectile.definitionId]
      const result = this.applyDamage(enemyId, damage, {
        towerId: projectile.towerId,
        projectileId: projectile.id,
        kind: hasSplash && !isPrimary ? 'splash' : 'direct',
        damageType: towerDef ? towerDamageType(towerDef) : 'ENERGY'
      })

      if (result.dealt > 0 && perf) {
        if (isPrimary || !hasSplash) {
          if (!primaryHit) {
            perf.hits += 1
            primaryHit = true
          }
        } else {
          perf.splashHits += 1
        }
      }

      const enemy = this.enemies.find((entry) => entry.id === enemyId && entry.alive)
      if (!enemy) continue
      const traits = statusTraitsFromDefinition(this.enemyCatalog[enemy.definitionId])
      for (const effect of projectile.effects) {
        const statusResult = applyStatus(enemy, effect, projectile.towerId, traits)
        if (statusResult === 'APPLIED' || statusResult === 'REFRESHED') {
          if (perf) {
            perf.statusApplications += 1
            if (effect.id === 'armorBreak') perf.armorBreakApplications += 1
          }
          if (effect.id === 'burn') {
            this.emitFx({
              type: 'burnApply',
              x: enemy.position.x,
              y: enemy.position.y,
              enemyId: enemy.id
            })
          }
          if (effect.id === 'armorBreak') {
            this.emitFx({
              type: 'armorBreakApply',
              x: enemy.position.x,
              y: enemy.position.y,
              enemyId: enemy.id,
              fracture: (projectile.towerLevel ?? 0) >= 4
            })
          }
        } else if (statusResult === 'IMMUNE' || statusResult === 'RESISTED') {
          this.emitStatusReject(enemy, statusResult, effect.id)
        }
      }
      if (isFrostLv4 && hadFrost) {
        const dfResult = applyStatus(
          enemy,
          {
            id: 'deepFreeze',
            duration: DEEP_FREEZE_DURATION,
            slow: 0,
            damagePerSecond: 0
          },
          projectile.towerId,
          traits
        )
        if (dfResult === 'APPLIED' || dfResult === 'REFRESHED') {
          if (perf) perf.statusApplications += 1
          this.emitFx({
            type: 'deepFreezeFlash',
            x: enemy.position.x,
            y: enemy.position.y
          })
        }
      }
    }
  }

  /** Throttled IMMUNE/RESISTED presentation — never spam per DoT tick. */
  private emitStatusReject(
    enemy: EnemyInstance,
    kind: 'IMMUNE' | 'RESISTED',
    statusId: string
  ): void {
    const key = enemy.id
    const readyAt = this.statusRejectCooldown.get(key) ?? 0
    if (this.elapsedTime < readyAt) return
    this.statusRejectCooldown.set(key, this.elapsedTime + STATUS_REJECT_COOLDOWN)
    this.emitFx({
      type: 'statusReject',
      x: enemy.position.x,
      y: enemy.position.y,
      enemyId: enemy.id,
      kind,
      statusId
    })
  }
}

function statusTraitsFromDefinition(
  definition: EnemyDefinition | undefined
): StatusHostTraits | undefined {
  if (!definition) return undefined
  return {
    immuneStatuses: definition.immuneStatuses,
    immuneStatusCategories: definition.immuneStatusCategories,
    statusDurationMultiplier: definition.statusDurationMultiplier ?? 1,
    statusCategoryDurationMultipliers: definition.statusCategoryDurationMultipliers,
    hasStatus: () => false
  }
}

/** Throttle window (sim seconds) between status-reject cues for one enemy. */
const STATUS_REJECT_COOLDOWN = 0.85

export { footprintCells }
